/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { PrimaryRecordResource, EditablePrimaryRecordResource } from '../../primary-record-types/MedicalDataTypes';
import { CreateResourceRequest, UpdateResourceBody } from '../../primary-record-types/apiTypes/medicalDataService';
import { useClearInMemoryAvatarCache } from '../useAvatar';
import { useMemo } from 'react';
import {
    getProfileResourceForCategoryCacheKey,
    getRelatedResourcesCacheKey,
    onNotesUpdate,
    onResourceRelatedCareTeamMemberUpdates,
    onResourceUpdate,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('UseResource', {
    resourceLoadFailure: 'Failed to load data',
    resourceUpdateSuccess: 'Successfully updated data',
    resourceUpdateFailure: 'Failed to update data',
    resourceDeleteSuccess: 'Successfully deleted data',
    resourceDeleteFailure: 'Failed to delete data',
    resourceCreateSuccess: 'Successfully created data',
    resourceCreateFailure: 'Failed to create data',
    relatedResourceLoadFailure: 'Failed to load related data',
    successfullyUpdatedRelatedResources: 'Successfully updated related data',
    failedToUpdateRelatedResources: 'Failed to update related data',
    successfullyUpdateResourceCareTeam: 'Successfully updated care team',
    failedToUpdateCareTeam: 'Failed to update care team',
});

export type RelatedResources = Record<
    MedicalDataTypes.PrimaryRecordResourceCategory,
    APITypes.medicalDataService.ListResourceResponseItem<PrimaryRecordResource>[]
>;

const defaultRelatedResources: RelatedResources = {
    condition: [],
    allergy: [],
    medication: [],
    visit: [],
    vaccination: [],
    procedure: [],
    imaging: [],
    lab: [],
};

export type UseResourceArgs = {
    medicalProfileId: string | null;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory | null;
    resourceId?: string;
    onCreated?: () => void;
    onDeleted?: () => void;
    onUpdated?: () => void;
    relatedResourceCategories?: MedicalDataTypes.PrimaryRecordResourceCategory[];
    onUpdateRelatedResourcesSuccess?: () => void;
    onUpdateResourceCareTeamSuccess?: () => void;
};

export type UpdateRelatedResourceArgs = {
    relatedResourceIds: string[];
    relatedResourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
};

export type UpdateRelatedCareTeamArgs = {
    careTeamMemberIds: string[];
    resourceGroupIds: string[];
};

export type UseResourceReturnValue<TResource extends PrimaryRecordResource> = {
    resource: APITypes.medicalDataService.ResourceDetailsResponse<TResource> | null;
    isLoadingResource: boolean;
    updateRelatedResources: (args: UpdateRelatedResourceArgs) => void;
    isUpdatingRelatedResources: boolean;
    relatedResources: RelatedResources;
    isLoadingRelatedResources: boolean;
    updateResource: (
        update: UpdateResourceBody<TResource extends EditablePrimaryRecordResource ? TResource : never>,
    ) => void;
    isUpdatingResource: boolean;
    deleteResource: (resourceId: string) => void;
    isDeletingResource: boolean;
    createResource: (
        create: CreateResourceRequest<TResource extends EditablePrimaryRecordResource ? TResource : never>,
    ) => void;
    isCreatingResource: boolean;
    onUpdateResourceCareTeam: (args: UpdateRelatedCareTeamArgs) => void;
    isUpdatingResourceCareTeam: boolean;
    relatedResourceGroupIds: string[];
};

export default function useResource<TResource extends PrimaryRecordResource>({
    medicalProfileId,
    resourceCategory,
    resourceId,
    onCreated,
    onDeleted,
    onUpdated,
    relatedResourceCategories,
    onUpdateResourceCareTeamSuccess,
    onUpdateRelatedResourcesSuccess,
}: UseResourceArgs): UseResourceReturnValue<TResource> {
    const notify = useNotify();
    const clearInMemoryAvatarCache = useClearInMemoryAvatarCache();
    const resourceQuery = useQuery({
        queryKey: getProfileResourceForCategoryCacheKey(
            medicalProfileId || '*',
            resourceCategory || '*',
            resourceId || '*',
        ),
        queryFn: async () => {
            return await appAPI.get(urls.api.medicalData.categoryAgnosticResource(medicalProfileId!, resourceId!));
        },
        enabled: !!resourceId && !!medicalProfileId,
    });
    useQueryErrorNotify(resourceQuery, intl('resourceLoadFailure'));

    const groupResourceIds = useMemo(() => {
        if (!resourceQuery.data) return [];

        const allIds = [resourceQuery.data.resource.id, ...resourceQuery.data.groupedResources.map((_: any) => _.id)];
        return [...new Set(allIds)].sort();
    }, [resourceQuery.data]);

    const updateRelatedResources = useMutation({
        mutationFn: async (args: UpdateRelatedResourceArgs) => {
            return await appAPI.put(
                urls.api.medicalData.relatedResources(
                    medicalProfileId!,
                    resourceCategory!,
                    resourceId!,
                    args.relatedResourceCategory,
                ),
                {
                    body: {
                        relatedResourceIds: args.relatedResourceIds,
                        groupResourceIds,
                    } as APITypes.medicalDataService.UpdateRelatedResourceRequest,
                },
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyUpdatedRelatedResources'));
            onResourceUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            onUpdateRelatedResourcesSuccess?.();
        },
        onError: () => {
            notify('error', intl('failedToUpdateRelatedResources'));
        },
    });

    const updateResource = useMutation({
        mutationFn: async (
            update: UpdateResourceBody<TResource extends EditablePrimaryRecordResource ? TResource : never>,
        ) => {
            return await appAPI.put(
                urls.api.medicalData.resource(medicalProfileId!, resourceCategory!, update.resourceUpdate.id!),
                {
                    body: update,
                },
            );
        },
        onSuccess: (_response, input) => {
            onUpdated?.();
            notify('success', intl('resourceUpdateSuccess'));
            onResourceUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            if (input.propagateStatusToGroup) {
                // The rest of the group is persisted by the table change queue, which can take
                // longer than the usual invalidation delay, so refresh again once it has drained.
                // The bulk edit path compensates the same way.
                setTimeout(() => {
                    onResourceUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
                }, 3000);
            }
            input.imageUpdates.forEach((imageUpdate) => {
                if (imageUpdate.type === 'update') {
                    clearInMemoryAvatarCache(
                        urls.api.medicalData.resourceImage(
                            medicalProfileId!,
                            resourceCategory!,
                            input.resourceUpdate.id!,
                            imageUpdate.resourceImageId,
                        ),
                    );
                }
            });
        },
        onError: () => {
            notify('error', intl('resourceUpdateFailure'));
        },
    });

    const deleteResource = useMutation({
        mutationFn: async (resourceId: string) => {
            return await appAPI.del(urls.api.medicalData.resource(medicalProfileId!, resourceCategory!, resourceId));
        },
        onSuccess: (_response) => {
            onDeleted?.();
            notify('success', intl('resourceDeleteSuccess'));
            onResourceUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
        },
        onError: () => {
            notify('error', intl('resourceDeleteFailure'));
        },
    });

    const createResource = useMutation({
        mutationFn: async (
            create: CreateResourceRequest<TResource extends EditablePrimaryRecordResource ? TResource : never>,
        ) => {
            return await appAPI.post(urls.api.medicalData.resources(medicalProfileId!, resourceCategory!), {
                body: create,
                // Create is non-idempotent: a silent SDK retry on a transient failure re-sends
                // the POST and creates duplicate resources (PR-2136). Mirrors the no-retry pattern
                // used for non-idempotent chat POSTs in useChat.ts. The onError toast surfaces a
                // failed create so the user can retry, rather than us retrying blindly.
                retryStrategy: {
                    strategy: 'no-retry',
                },
            });
        },
        retry: false,
        onSuccess: (_response, create) => {
            onCreated?.();
            notify('success', intl('resourceCreateSuccess'));
            onResourceUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            if (create.careTeamMemberId)
                onResourceRelatedCareTeamMemberUpdates.resetCache({
                    medicalProfileId: medicalProfileId!,
                    resourceCategory: resourceCategory!,
                })();
            // The attached note is created server-side, bypassing the note mutations that
            // normally reset the notes caches — reset them here or the profile's notes
            // lists/counts stay stale.
            if (create.sourceNoteId) onNotesUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
        },
        onError: () => {
            notify('error', intl('resourceCreateFailure'));
        },
    });

    const updateResourceCareTeam = useMutation({
        mutationFn: async ({ careTeamMemberIds, resourceGroupIds }: UpdateRelatedCareTeamArgs) => {
            return await appAPI.put(
                urls.api.medicalData.resourceCareTeam(medicalProfileId!, resourceCategory!, resourceId!),
                {
                    body: {
                        careTeamMemberIds,
                        resourceGroupIds,
                    } as APITypes.medicalDataService.UpdateResourceCareTeamRequest,
                },
            );
        },
        onSuccess: (_response) => {
            onUpdateResourceCareTeamSuccess?.();
            notify('success', intl('successfullyUpdateResourceCareTeam'));
            onResourceRelatedCareTeamMemberUpdates.resetCache({
                medicalProfileId: medicalProfileId!,
                resourceCategory: resourceCategory!,
            })();
        },
        onError: () => {
            notify('error', intl('failedToUpdateCareTeam'));
        },
    });

    const relatedResourcesQuery = useQuery({
        // Key on everything the queryFn reads. Callers that only need the mutation pass no categories, and
        // sharing one cache entry with the details screen let a refetch run their empty queryFn over its data.
        queryKey: [
            ...getRelatedResourcesCacheKey(medicalProfileId!, resourceCategory!, resourceId!),
            relatedResourceCategories || [],
            groupResourceIds,
        ],
        queryFn: async () => {
            const relatedResources: RelatedResources = {
                ...defaultRelatedResources,
            };
            await Promise.all(
                (relatedResourceCategories || []).map(async (category) => {
                    const response = (await appAPI.post(
                        urls.api.medicalData.groupRelatedResources(medicalProfileId!, resourceCategory!, category),
                        {
                            body: {
                                groupResourceIds: groupResourceIds,
                            } as APITypes.medicalDataService.ResourceGroupRelatedResourcesRequest,
                        },
                    )) as APITypes.medicalDataService.ResourceGroupRelatedResourcesResponse;
                    relatedResources[category] = response.resources;
                }),
            );
            return relatedResources;
        },
        enabled:
            !!relatedResourceCategories?.length &&
            !!groupResourceIds.length &&
            !!medicalProfileId &&
            !!resourceCategory &&
            !!resourceId,
    });
    useQueryErrorNotify(relatedResourcesQuery, intl('relatedResourceLoadFailure'));

    return {
        resource: resourceQuery.data || null,
        isLoadingResource: resourceQuery.isLoading,
        updateResource: updateResource.mutateAsync,
        isUpdatingResource: updateResource.isPending,
        deleteResource: deleteResource.mutateAsync,
        isDeletingResource: deleteResource.isPending,
        createResource: createResource.mutateAsync,
        isCreatingResource: createResource.isPending,
        relatedResources: relatedResourcesQuery.data || defaultRelatedResources,
        isLoadingRelatedResources: relatedResourcesQuery.isLoading,
        updateRelatedResources: updateRelatedResources.mutate,
        isUpdatingRelatedResources: updateRelatedResources.isPending,
        isUpdatingResourceCareTeam: updateResourceCareTeam.isPending,
        onUpdateResourceCareTeam: updateResourceCareTeam.mutate,
        relatedResourceGroupIds: groupResourceIds,
    };
}

export type UseResourceCategoryParams = {
    medicalProfileId: string | null;
    resourceId: string | null;
};

export type UseResourceCategoryReturnValue = {
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory | null;
    isLoading: boolean;
};

/**
 * Simplified hook to get the resource category given a resource ID.
 *
 * If the resource ID is null, this hook will return null.
 */
export const useResourceCategory = ({
    medicalProfileId,
    resourceId,
}: UseResourceCategoryParams): UseResourceCategoryReturnValue => {
    const { resource, isLoadingResource } = useResource({
        medicalProfileId,
        resourceCategory: null,
        resourceId: resourceId || undefined,
    });

    if (!resourceId) return { resourceCategory: null, isLoading: false };

    if (isLoadingResource) return { resourceCategory: null, isLoading: true };

    if (!resource) return { resourceCategory: null, isLoading: false };

    return {
        resourceCategory: resource.resource.type,
        isLoading: false,
    };
};
